import React from 'react';
import Select, { components, MenuProps, SingleValue, StylesConfig } from 'react-select';

export interface CompanyOption {
  id: number;
  name: string;
  domain?: string | null;
}

interface Props {
  value: number | null | undefined;
  companies: CompanyOption[];
  onChange: (value: number | null) => void;
  onCreateNew?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  borderColor?: string;
  placeholder?: string;
}

interface SelectOption {
  value: number;
  label: string;
  domain?: string | null;
}

/**
 * Searchable company dropdown, mirroring SectorSelect's UX. Includes an
 * optional "+ Create new company" menu item that calls onCreateNew() so the
 * parent can swap to an inline create form.
 */
export const CompanyPicker: React.FC<Props> = ({
  value,
  companies,
  onChange,
  onCreateNew,
  isLoading = false,
  isDisabled = false,
  borderColor = '#E5E7EB',
  placeholder = 'Search and select a company...',
}) => {
  const options: SelectOption[] = companies.map((c) => ({
    value: c.id,
    label: c.name,
    domain: c.domain ?? null,
  }));
  const selected = options.find((o) => o.value === value) || null;

  const handleChange = (option: SingleValue<SelectOption>) => {
    onChange(option ? option.value : null);
  };

  const styles: StylesConfig<SelectOption, false> = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#3B82F6' : borderColor,
      borderRadius: '0.5rem',
      padding: '2px 4px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.3)' : 'none',
      '&:hover': { borderColor: state.isFocused ? '#3B82F6' : borderColor },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      zIndex: 50,
      overflow: 'hidden',
    }),
  };

  // Render company name + (greyed) domain in each option for disambiguation.
  const OptionLabel = ({ data }: { data: SelectOption }) => (
    <div className="flex flex-col leading-tight">
      <span>{data.label}</span>
      {data.domain && (
        <span className="text-xs text-gray-500">{data.domain}</span>
      )}
    </div>
  );

  const MenuWithAdd = (menuProps: MenuProps<SelectOption, false>) => (
    <components.Menu {...menuProps}>
      <button
        type="button"
        onMouseDown={(e) => {
          // Stop react-select from treating this as an option click / blur.
          e.preventDefault();
          e.stopPropagation();
          onCreateNew?.();
        }}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b transition-colors hover:bg-blue-50"
        style={{ color: '#2563EB', borderColor: '#E5E7EB' }}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
        Create new company
      </button>
      {menuProps.children}
    </components.Menu>
  );

  return (
    <Select<SelectOption>
      options={options}
      value={selected}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable
      isSearchable
      isLoading={isLoading}
      isDisabled={isDisabled}
      styles={styles}
      components={{
        ...(onCreateNew ? { Menu: MenuWithAdd } : {}),
        Option: (optionProps) => (
          <components.Option {...optionProps}>
            <OptionLabel data={optionProps.data} />
          </components.Option>
        ),
      }}
      noOptionsMessage={() =>
        onCreateNew
          ? 'No companies match — click "Create new company" above'
          : 'No companies found'
      }
    />
  );
};
